import { AlertCircle, CheckCircle2, ClipboardPaste, Download, FileSpreadsheet, UploadCloud, X } from "lucide-react";
import { ChangeEvent, DragEvent, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { parseScoreColumn, toScore } from "../scorePaste";
import type { Gradebook, ImportResult, ImportRow, Student } from "../types";

type ImportModalProps = {
  gradebook: Gradebook;
  orderedStudents: Student[];
  initialColumnId?: string;
  initialComponent?: "multipleChoice" | "written";
  initialClipboard?: string;
  initialStartStudentCode?: string;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
};

function normalizeHeader(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll("đ", "d")
    .replaceAll("Đ", "D")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export default function ImportModal({ gradebook, orderedStudents, initialColumnId, initialComponent, initialClipboard = "", initialStartStudentCode, onClose, onImported }: ImportModalProps) {
  const [columnId, setColumnId] = useState(initialColumnId || gradebook.columns[0]?.id || "");
  const [component, setComponent] = useState<"multipleChoice" | "written">(initialComponent || "multipleChoice");
  const [sourceMode, setSourceMode] = useState<"file" | "clipboard">(initialClipboard ? "clipboard" : "file");
  const [clipboard, setClipboard] = useState(initialClipboard);
  const [startStudentCode, setStartStudentCode] = useState(initialStartStudentCode || orderedStudents[0]?.studentCode || "");
  const [fileName, setFileName] = useState("");
  const [fileRows, setFileRows] = useState<ImportRow[]>([]);
  const [parseError, setParseError] = useState("");
  const [sourceNote, setSourceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedColumn = gradebook.columns.find((column) => column.id === columnId);
  const componentLabel = component === "multipleChoice" ? "Trắc nghiệm" : "Tự luận";
  const clipboardResult = useMemo(() => parseScoreColumn(clipboard, orderedStudents, startStudentCode), [clipboard, orderedStudents, startStudentCode]);
  const rows = sourceMode === "clipboard" ? clipboardResult.rows : fileRows;
  const currentError = sourceMode === "clipboard" ? clipboardResult.error || parseError : parseError;

  const validation = useMemo(() => {
    const studentByCode = new Map(gradebook.students.map((student) => [student.studentCode.trim(), student]));
    const invalid = rows.filter((row) => {
      if (row.value !== null && (!Number.isFinite(row.value) || row.value < 0 || row.value > 10)) return true;
      if (selectedColumn?.mode !== "sum" || row.value === null) return false;
      const other = studentByCode.get(row.studentCode)?.components[columnId]?.[component === "multipleChoice" ? "written" : "multipleChoice"];
      return typeof other === "number" && row.value + other > 10;
    });
    const knownCodes = new Set(gradebook.students.map((student) => student.studentCode.trim()));
    const matchedCount = rows.filter((row) => knownCodes.has(row.studentCode)).length;
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const row of rows) {
      if (seen.has(row.studentCode)) duplicates.add(row.studentCode);
      seen.add(row.studentCode);
    }
    return { invalid, matchedCount, unmatchedCount: rows.length - matchedCount, duplicates: [...duplicates] };
  }, [rows, gradebook.students, selectedColumn, columnId, component]);

  const downloadTemplate = async () => {
    const XLSX = await import("xlsx");
    const data = gradebook.students.map((student) => ({
      SBD: student.studentCode,
      "Họ và tên lót": student.lastName,
      Tên: student.firstName,
      [`Điểm (${selectedColumn?.shortLabel || ""}${selectedColumn?.mode === "sum" ? ` · ${componentLabel}` : ""})`]: selectedColumn?.mode === "sum" ? student.components[columnId]?.[component] ?? "" : student.scores[columnId] ?? "",
    }));
    const sheet = XLSX.utils.json_to_sheet(data);
    sheet["!cols"] = [{ wch: 14 }, { wch: 24 }, { wch: 14 }, { wch: 20 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, gradebook.class.name);
    XLSX.writeFile(workbook, `Mau_nhap_diem_${gradebook.class.name}_${selectedColumn?.key || "diem"}.xlsx`);
  };

  const parseFile = async (file: File) => {
    setParseError("");
    setSourceNote("");
    setFileRows([]);
    setFileName(file.name);
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      let parsedRows: ImportRow[] = [];
      let detectedSheet = "";
      let detectedHeaderRow = 0;

      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) continue;
        const firstSheetRow = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]).s.r + 1 : 1;
        const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: false });
        for (let headerIndex = 0; headerIndex < Math.min(matrix.length, 20); headerIndex += 1) {
          const headers = (matrix[headerIndex] || []).map((cell) => normalizeHeader(String(cell ?? "")));
          const codeIndex = headers.findIndex((header) => ["sbd", "sobaodanh", "mahocsinh", "studentcode"].includes(header));
          const scoreIndex = headers.findIndex((header) => header === "diem" || header.startsWith("diem"));
          if (codeIndex < 0 || scoreIndex < 0) continue;

          const lastNameIndex = headers.findIndex((header) => header === "hovatenlot");
          const firstNameIndex = headers.findIndex((header) => header === "ten");
          const fullNameIndex = headers.findIndex((header) => ["hoten", "hovaten"].includes(header));
          parsedRows = matrix.slice(headerIndex + 1).map((row) => {
            let lastName = lastNameIndex >= 0 ? String(row[lastNameIndex] ?? "").trim() : "";
            let firstName = firstNameIndex >= 0 ? String(row[firstNameIndex] ?? "").trim() : "";
            if ((!lastName || !firstName) && fullNameIndex >= 0) {
              const parts = String(row[fullNameIndex] ?? "").trim().replace(/\s+/g, " ").split(" ").filter(Boolean);
              if (parts.length >= 2) {
                lastName = parts.slice(0, -1).join(" ");
                firstName = parts.at(-1) || "";
              }
            }
            return {
              studentCode: String(row[codeIndex] ?? "").trim(),
              lastName,
              firstName,
              value: toScore(row[scoreIndex]),
            };
          }).filter((row) => row.studentCode);
          detectedSheet = sheetName;
          detectedHeaderRow = firstSheetRow + headerIndex;
          break;
        }
        if (parsedRows.length) break;
      }

      if (!parsedRows.length) throw new Error("Không tìm thấy cột “Số báo danh” và “Điểm” trong 20 dòng đầu của các trang tính.");
      if (parsedRows.length > 2_000) throw new Error("Tệp điểm vượt quá giới hạn 2.000 học sinh.");
      setSourceNote(`Đã nhận dạng trang “${detectedSheet}”, tiêu đề dòng ${detectedHeaderRow}${normalizeHeader(detectedSheet).includes("ketquathi") ? " · chuẩn OLM" : ""}.`);
      setFileRows(parsedRows);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "Không thể đọc tệp này.");
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void parseFile(file);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) void parseFile(file);
  };

  const submit = async () => {
    if (!columnId || rows.length === 0 || currentError || validation.invalid.length > 0 || validation.duplicates.length > 0 || validation.matchedCount === 0 || submitting) return;
    setSubmitting(true);
    setParseError("");
    try {
      const result = await api.importScores(gradebook.class.id, columnId, rows, selectedColumn?.mode === "sum" ? component : undefined);
      onImported(result);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "Không thể nhập điểm.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="import-modal" role="dialog" aria-modal="true" aria-labelledby="import-title">
        <header>
          <div>
            <span className="modal-kicker">NHẬP DỮ LIỆU</span>
            <h2 id="import-title">Cập nhật điểm từ Excel</h2>
            <p>Tải file có SBD hoặc dán một cột điểm từ Excel theo đúng thứ tự học sinh.</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Đóng"><X /></button>
        </header>

        <div className="import-step">
          <div className="step-number">1</div>
          <div className="step-content">
            <label className="field-label" htmlFor="score-column">Chọn cột điểm cần ghi đè</label>
            <select id="score-column" value={columnId} onChange={(event) => { setColumnId(event.target.value); setFileRows([]); setFileName(""); setClipboard(""); setParseError(""); }}>
              {gradebook.columns.map((column) => <option key={column.id} value={column.id}>{column.label} — {column.shortLabel}</option>)}
            </select>
            {selectedColumn?.mode === "sum" && <div className="import-component-choice"><span>Nhập phần điểm</span><label><input type="radio" name="import-component" checked={component === "multipleChoice"} onChange={() => setComponent("multipleChoice")} /> Trắc nghiệm</label><label><input type="radio" name="import-component" checked={component === "written"} onChange={() => setComponent("written")} /> Tự luận</label><small>Tổng tự tính khi cả hai phần đã có điểm.</small></div>}
            <button className="template-button" onClick={() => void downloadTemplate()}><Download size={17} /> Tải file mẫu cho {gradebook.class.name}</button>
          </div>
        </div>

        <div className="import-mode-tabs" role="tablist" aria-label="Cách nhập điểm">
          <button type="button" role="tab" aria-selected={sourceMode === "file"} className={sourceMode === "file" ? "active" : ""} onClick={() => { setSourceMode("file"); setParseError(""); }}><FileSpreadsheet /> Từ file Excel</button>
          <button type="button" role="tab" aria-selected={sourceMode === "clipboard"} className={sourceMode === "clipboard" ? "active" : ""} onClick={() => { setSourceMode("clipboard"); setParseError(""); }}><ClipboardPaste /> Dán cột điểm</button>
        </div>

        <div className="import-step">
          <div className="step-number">2</div>
          <div className="step-content">
            {sourceMode === "file" ? <>
              <span className="field-label">Tải file đã điền điểm</span>
              <div className={`drop-zone ${rows.length ? "drop-zone--ready" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={onDrop} onClick={() => inputRef.current?.click()}>
                <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange} hidden />
                {rows.length ? <CheckCircle2 className="drop-success" /> : <UploadCloud />}
                <strong>{rows.length ? fileName : "Kéo thả file vào đây"}</strong>
                <span>{rows.length ? `${rows.length} dòng đã đọc${sourceNote ? ` · ${sourceNote}` : ""}` : "hoặc bấm để chọn tệp .xlsx, .xls, .csv (hỗ trợ file OLM)"}</span>
              </div>
            </> : <>
              <label className="field-label" htmlFor="score-start-student">Bắt đầu từ học sinh</label>
              <select id="score-start-student" value={startStudentCode} onChange={(event) => setStartStudentCode(event.target.value)}>
                {orderedStudents.map((student, index) => <option key={student.id} value={student.studentCode}>{String(index + 1).padStart(2, "0")} · {student.studentCode} · {student.lastName} {student.firstName}</option>)}
              </select>
              <p className="score-paste-help">Mỗi dòng Excel ứng với một học sinh theo thứ tự đang hiển thị trong bảng. Dòng trống ở giữa sẽ xóa điểm tương ứng.</p>
              <label className="field-label" htmlFor="score-clipboard">Dán một cột điểm</label>
              <textarea id="score-clipboard" className="score-paste-textarea" value={clipboard} onChange={(event) => { setClipboard(event.target.value); setParseError(""); }} placeholder={"8.75\n6\n7.1\n5.7"} spellCheck={false} />
            </>}
          </div>
        </div>

        {currentError && <div className="import-alert import-alert--error"><AlertCircle /> <span>{currentError}</span></div>}
        {rows.length > 0 && validation.matchedCount === 0 && <div className="import-alert import-alert--error"><AlertCircle /> <span>Không có SBD nào trong tệp khớp với lớp {gradebook.class.name}. Hãy kiểm tra lớp đã chọn.</span></div>}
        {rows.length > 0 && validation.duplicates.length > 0 && <div className="import-alert import-alert--error"><AlertCircle /> <span>Tệp có {validation.duplicates.length} SBD bị lặp. Hãy sửa trước khi nhập điểm.</span></div>}

        {rows.length > 0 && (
          <div className="import-preview">
            <div className="import-preview__head">
              <div><FileSpreadsheet /><span><b>Xem trước dữ liệu</b><small>{validation.matchedCount} SBD khớp · {validation.unmatchedCount} không khớp · {validation.invalid.length} điểm lỗi</small></span></div>
              {validation.invalid.length === 0 && validation.duplicates.length === 0 && validation.matchedCount > 0 && <span className="valid-badge"><CheckCircle2 /> Sẵn sàng</span>}
            </div>
            <div className="preview-table-wrap">
              <table>
                <thead><tr><th>SBD</th><th>Họ và tên lót</th><th>Tên</th><th>{selectedColumn?.mode === "sum" ? componentLabel : selectedColumn?.shortLabel}</th></tr></thead>
                <tbody>
                  {(sourceMode === "clipboard" ? rows : rows.slice(0, 5)).map((row, index) => (
                    <tr key={`${row.studentCode}-${index}`} className={validation.invalid.includes(row) ? "row-invalid" : ""}>
                      <td>{row.studentCode}</td><td>{row.lastName || "—"}</td><td>{row.firstName || "—"}</td><td><b>{row.value === null ? "— (xóa)" : Number.isNaN(row.value) ? "Không hợp lệ" : row.value}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sourceMode === "file" && rows.length > 5 && <div className="preview-more">và {rows.length - 5} dòng khác…</div>}
            </div>
          </div>
        )}

        <footer>
          <button className="btn-secondary" onClick={onClose}>Hủy</button>
          <button className="btn-primary-small" disabled={!rows.length || !!currentError || validation.invalid.length > 0 || validation.duplicates.length > 0 || validation.matchedCount === 0 || submitting} onClick={submit}>
            {submitting ? <span className="spinner" /> : <><UploadCloud size={17} /> Ghi đè {validation.matchedCount || ""} điểm</>}
          </button>
        </footer>
      </section>
    </div>
  );
}
